import React from 'react';
import { motion } from 'framer-motion';
import { Target, Award, Shield } from 'lucide-react';

const MissionSection = () => {
  const missionPoints = [
    {
      icon: Target,
      title: "Our Mission",
      text: "For over 30 years, AEE has set the standard in GMP cleanroom solutions — delivering precision-engineered controlled environments for biopharma and healthcare."
    },
    {
      icon: Award,
      title: "Our Expertise",
      text: "Leveraging over 30 years of expertise, AEE delivers industry-leading cleanroom solutions for biopharma and healthcare — where precision and compliance meet innovation."
    },
    {
      icon: Shield,
      title: "Our Promise",
      text: "AEE sets the standard in GMP cleanroom technology — delivering trusted, compliant controlled environment solutions to biopharma and healthcare for over 30 years."
    }
  ];

  return (
    <section id="mission" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background - Dark with Glass Effect */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #1C4B8A 0%, #0F2847 50%, #0a1929 100%)'
        }}
      />
      
      {/* Glass Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(138, 189, 224, 0.4) 0%, transparent 70%)'
          }}
        />
        <div 
          className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full opacity-15"
          style={{
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)'
          }}
        />
      </div>
      
      <div className="max-w-6xl mx-auto px-5 md:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="badge-minimal-dark mb-6">
            Our Mission
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Defining the Standard in Cleanroom Environment Excellence
          </h2>
        </motion.div>

        {/* Glass Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {missionPoints.map((point, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="group p-8 rounded-3xl transition-all duration-500 hover:-translate-y-2"
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
              }}
            >
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.15)'
                }}
              >
                <point.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">{point.title}</h3>
              <p className="text-white/70 leading-relaxed">{point.text}</p>
            </motion.div>
          ))}
        </div>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-16 text-white/50 italic text-lg"
        >
          "Helping you create specialty working environments since 1989"
        </motion.p>
      </div>
    </section>
  );
};

export default MissionSection;
